import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowLeft, Check, ArrowRight, MapPin } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import PropertyCard from "../components/PropertyCard";
import Seo from "../components/Seo";
import IdxDisclaimer from "../components/IdxDisclaimer";

const centerPin = new L.DivIcon({
  className: "gens-map-pin",
  html: `<div style="width:30px;height:38px;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));">
    <svg width="30" height="38" viewBox="0 0 30 38"><path d="M15 0C6.7 0 0 6.7 0 15c0 11.3 15 23 15 23s15-11.7 15-23C30 6.7 23.3 0 15 0z" fill="#E63946"/><circle cx="15" cy="15" r="6" fill="#fff"/></svg>
  </div>`,
  iconSize: [30, 38], iconAnchor: [15, 38], popupAnchor: [0, -34],
});
const mlsPin = (price) => new L.DivIcon({
  className: "mls-price-pin",
  html: `<div style="background:#1A1A1A;color:#fff;font-family:'Montserrat',system-ui,sans-serif;font-weight:700;font-size:10px;padding:3px 6px;border-radius:12px;box-shadow:0 2px 8px rgba(0,0,0,0.3);border:1.5px solid #fff;white-space:nowrap;">$${(Math.round(price/1000)||0)}K</div>`,
  iconSize: [46, 20], iconAnchor: [23, 20],
});

const fmt = (n) => (n ? `$${Number(n).toLocaleString()}` : "—");

export default function NeighborhoodDetail() {
  const { slug } = useParams();
  const [n, setN] = useState(null);
  const [props, setProps] = useState([]);
  const [mls, setMls] = useState({ items: [], total: 0, filter: {} });

  useEffect(() => {
    api.get(`/neighborhoods/${slug}`).then((r) => {
      setN(r.data);
      api.get("/properties", { params: { neighborhood: r.data.name } }).then((rr) => setProps(rr.data)).catch(() => {});
      api.get(`/mls/listings/by-neighborhood/${slug}?limit=12`).then((rr) => setMls(rr.data)).catch(() => setMls({ items: [], total: 0, filter: {} }));
    }).catch(() => setN(false));
  }, [slug]);

  if (n === false) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h1 className="font-display text-4xl">Neighborhood not found.</h1>
        <Link to="/neighborhoods" className="text-[#E63946] mt-6 inline-block">← Back</Link>
      </div>
    );
  }
  if (!n) return <div className="py-32 text-center text-gray-500">Loading…</div>;

  // Build browse-more URL preserving the filter
  const browseParams = new URLSearchParams();
  if (mls.filter?.city) browseParams.set("city", mls.filter.city);
  if (mls.filter?.zip) browseParams.set("q", mls.filter.zip);
  const browseUrl = `/mls-listings${browseParams.toString() ? `?${browseParams}` : ""}`;

  return (
    <div className="bg-stone-50 min-h-screen" data-testid={`neighborhood-detail-${n.slug}`}>
      <Seo
        title={`${n.name}, Houston`}
        description={`${n.tagline} — ${n.description.slice(0, 160)}`}
        image={n.image}
        path={`/neighborhoods/${n.slug}`}
      />
      {/* Hero */}
      <section className="relative h-[60vh] min-h-[420px] overflow-hidden">
        <img src={n.image} alt={n.name} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 h-full flex flex-col justify-end pb-12 text-white">
          <Link to="/neighborhoods" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-white mb-5">
            <ArrowLeft className="w-4 h-4" /> All neighborhoods
          </Link>
          <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-3">Houston · Texas</div>
          <h1 className="font-display text-5xl lg:text-7xl font-light tracking-tighter">{n.name}</h1>
          <p className="text-white/85 mt-4 max-w-xl text-lg">{n.tagline}</p>
        </div>
      </section>

      {/* Stats + description + mini map */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <h2 className="font-display text-3xl font-semibold mb-5">About {n.name}</h2>
          <p className="text-[#444] leading-relaxed text-lg">{n.description}</p>

          <h3 className="font-display text-xl font-semibold mt-12 mb-4">Neighborhood highlights</h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {n.highlights.map((h) => (
              <li key={h} className="flex items-center gap-3 text-sm">
                <span className="w-6 h-6 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center">
                  <Check className="w-3.5 h-3.5" strokeWidth={2.5} />
                </span>
                {h}
              </li>
            ))}
          </ul>
        </div>
        <aside className="lg:col-span-5 space-y-6">
          <div className="bg-white border border-gray-200 rounded-2xl p-7 grid grid-cols-2 gap-6">
            <Stat label="Median price" value={formatPrice(n.median_price)} />
            <Stat label="Avg $/sqft" value={`$${n.avg_sqft_price}`} />
            <Stat label="Active MLS listings" value={mls.total} />
            <Stat label="ZIP / City" value={n.mls_zip || n.mls_city || "—"} />
          </div>
          {n.lat && n.lng && (
            <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden h-[280px]" data-testid={`nbr-map-${n.slug}`}>
              <MapContainer center={[n.lat, n.lng]} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[n.lat, n.lng]} icon={centerPin}>
                  <Popup>{n.name}</Popup>
                </Marker>
                {mls.items.filter((l) => l.lat && l.lng).slice(0, 30).map((l) => (
                  <Marker key={l.listing_key} position={[l.lat, l.lng]} icon={mlsPin(l.price)}>
                    <Popup>
                      <div className="text-xs">
                        <div className="font-semibold">{fmt(l.price)}</div>
                        <div className="text-gray-600">{l.address}</div>
                        <Link to={`/mls-listings/${l.listing_key}`} className="text-[#E63946] text-[11px] font-semibold">View →</Link>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </aside>
      </section>

      {/* Live HAR listings */}
      {mls.items.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20" data-testid={`nbr-mls-${n.slug}`}>
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-2">Live HAR Feed</div>
              <h2 className="font-display text-3xl font-semibold">
                {mls.total} active {mls.total === 1 ? "listing" : "listings"} in {n.name}
              </h2>
            </div>
            <Link to={browseUrl} className="text-sm text-[#E63946] hover:text-[#c9313d] font-semibold flex items-center gap-1" data-testid={`nbr-see-all-${n.slug}`}>
              Browse all {n.name} homes <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {mls.items.slice(0, 6).map((l) => (
              <Link key={l.listing_key} to={`/mls-listings/${l.listing_key}`} className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-shadow group" data-testid={`nbr-mls-card-${l.listing_key}`}>
                <div className="relative aspect-[4/3] bg-stone-100">
                  {l.photos?.[0] ? (
                    <img src={l.photos[0]} alt={l.address} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-300">No photo</div>
                  )}
                </div>
                <div className="p-5">
                  <div className="font-display text-2xl font-semibold">{fmt(l.price)}</div>
                  <div className="text-sm text-[#1A1A1A] mt-1 truncate">{l.address}</div>
                  <div className="text-xs text-gray-500">{l.city}, TX {l.zip}</div>
                  <div className="text-xs text-gray-600 mt-2">{l.beds} bd · {l.baths} ba · {l.sqft?.toLocaleString?.() || l.sqft} sqft</div>
                  <div className="text-[10px] text-gray-400 mt-2 truncate">{l.list_office} · MLS #{l.listing_key}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Curated Gens Realty listings */}
      {props.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
          <h2 className="font-display text-3xl font-semibold mb-8">Gens Realty exclusives in {n.name}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {props.map((p) => <PropertyCard key={p.id} p={p} />)}
          </div>
        </section>
      )}

      {/* Empty state fallback */}
      {mls.items.length === 0 && props.length === 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
          <div className="bg-white border border-dashed border-gray-200 rounded-2xl p-12 text-center">
            <MapPin className="w-8 h-8 text-gray-300 mx-auto mb-3" />
            <div className="font-display text-2xl text-[#1A1A1A] mb-2">No active listings right now</div>
            <p className="text-sm text-gray-500 mb-6">The HAR feed refreshes nightly. Check back soon, or browse the full MLS.</p>
            <Link to="/mls-listings" className="inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#c9313d] text-white px-5 py-2.5 rounded-md text-sm font-semibold">
              Browse all Houston listings <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}

      {/* IDX Disclaimer — only render if any MLS data was shown */}
      {mls.items.length > 0 && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16">
          <IdxDisclaimer />
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-gray-500 mb-1">{label}</div>
      <div className="font-display text-2xl font-semibold tracking-tight text-[#1A1A1A]">{value}</div>
    </div>
  );
}
